import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import { darken } from "@material-ui/core/styles/colorManipulator";
import TableCell from "@material-ui/core/TableCell";
import MuiTableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import classNames from "classnames";
import { sortBy, uniq } from "lodash";
import React from "react";
import { TableHeaderFooterColumn } from "../models";
import { TablePagingProps } from "./Table";

const styles = (theme: Theme) =>
  createStyles({
    tableFooterRow: {
      height: 48,
    },
    tableFooterCell: {
      borderBottomColor: theme.palette.divider,
      // Background color must be defined on cell instead of row, so that background-color remains fixed with
      // position: sticky
      backgroundColor: darken(theme.palette.background.default, 0.05),
      color: theme.palette.text.primary,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: "bold",
    },
    tableFooterBorderRow: {
      height: 1,
    },
    tableFooterBorderCell: {
      position: "sticky",
      bottom: 48,
      zIndex: 1,
      border: "none",
      padding: 0,
      "&:last-child": {
        padding: 0,
      },
    },
    tableFooterBorder: {
      margin: 0,
      border: 0,
      height: 1,
      backgroundColor: theme.palette.divider,
    },
    pagination: {
      backgroundColor: darken(theme.palette.background.default, 0.05),
      color: theme.palette.text.primary,
      paddingTop: 0,
      paddingBottom: 0,
    },
    stickyPagination: {
      position: "sticky",
      bottom: -1,
      zIndex: 1,
    },
    paginationRoot: {
      fontSize: theme.typography.body2.fontSize,
    },
    paginationCaption: {
      fontSize: theme.typography.body2.fontSize,
    },
    paginationSelectRoot: {
      fontSize: theme.typography.body2.fontSize,
    },
    paginationSelect: {
      paddingTop: 9,
      paddingRight: theme.spacing(3),
    },
    paginationSelectIcon: {
      top: 4,
    },
    paginationToolbar: {
      height: 48,
      minHeight: 48,
    },
  });

interface TableFooterProps extends WithStyles<typeof styles> {
  id?: string;
  paging?: TablePagingProps;
  footerRow?: TableHeaderFooterColumn[];
  sticky?: boolean;
}

export const TableFooterComponent = (props: TableFooterProps) => {
  const { id, paging, footerRow, sticky, classes } = props;

  const footerRows: JSX.Element[] = [];

  if (footerRow) {
    footerRows.push(
      <TableRow key="fr" className={classes.tableFooterRow}>
        {footerRow.map((footerRowColumn, index) => (
          <TableCell
            key={`frc-${index}`}
            padding={footerRowColumn.padding}
            size={footerRowColumn.size}
            align={footerRowColumn.align}
            colSpan={footerRowColumn.colSpan}
            className={classes.tableFooterCell}
            style={{ display: footerRowColumn.hidden ? "none" : undefined }}
          >
            {footerRowColumn.content}
          </TableCell>
        ))}
      </TableRow>,
    );
  }

  if (paging) {
    const { pageIndex, pageSize, totalCount, onPageIndexChange, onPageSizeChange } = paging;

    const paginationClassName = classNames(classes.pagination, sticky ? classes.stickyPagination : undefined);

    const handlePageIndexChange = (event: any, index: number) => {
      onPageIndexChange(index);
    };

    const handlePageSizeChange = (event: any) => {
      const size = event.target.value;
      onPageSizeChange(size);
    };

    // Add pageSize as rows per page options if it does not already exist
    const rowsPerPageOptions = sortBy(uniq([5, 10, 25, 50, 100, pageSize]));

    footerRows.push(
      <TableRow key="fr_border" className={classes.tableFooterBorderRow}>
        <TableCell className={classes.tableFooterBorderCell} colSpan={1000}>
          <hr className={classes.tableFooterBorder} />
        </TableCell>
      </TableRow>,
    );

    footerRows.push(
      <TableRow key="fr_paging" className={classes.tableFooterRow}>
        <TablePagination
          classes={{
            root: classes.paginationRoot,
            caption: classes.paginationCaption,
            selectRoot: classes.paginationSelectRoot,
            select: classes.paginationSelect,
            selectIcon: classes.paginationSelectIcon,
            toolbar: classes.paginationToolbar,
          }}
          className={paginationClassName}
          count={totalCount ?? 0}
          labelRowsPerPage={"Rows:"}
          rowsPerPage={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          page={pageIndex}
          onChangePage={handlePageIndexChange}
          onChangeRowsPerPage={handlePageSizeChange}
        />
      </TableRow>,
    );
  }

  return footerRows.length ? <MuiTableFooter id={id}>{footerRows}</MuiTableFooter> : null;
};

export const TableFooter = withStyles(styles)(TableFooterComponent);
